/**
 * <pg-search>: predictive search inside the search dialog (snippets/pg-search-dialog.liquid).
 * Results are server-rendered by sections/pg-predictive-search.liquid, fetched through the
 * Section Rendering API as the shopper types (debounced; stale requests are aborted).
 * Arrow keys move between results; Enter on the field submits a full search.
 */
import { PgElement, define, debounce, config } from '@pg/core';
import { fetchSection, parseSection } from '@pg/section-renderer';

class PgSearch extends PgElement {
  #initial = '';

  mount() {
    const { input, results } = this.refs;
    this.#initial = results.innerHTML;

    const search = debounce(() => this.#search(), 200);
    input.addEventListener('input', search, { signal: this.signal });
    input.addEventListener('keydown', (event) => this.#onKeydown(event), { signal: this.signal });
    results.addEventListener('keydown', (event) => this.#onKeydown(event), { signal: this.signal });

    this.closest('dialog')?.addEventListener('pg:dialog:open', () => requestAnimationFrame(() => input.focus()), {
      signal: this.signal,
    });
  }

  async #search() {
    const { input, results } = this.refs;
    const terms = input.value.trim();
    if (!terms) {
      results.innerHTML = this.#initial;
      return;
    }
    const url = new URL(config.routes.predictiveSearch, location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('resources[type]', 'product,collection,query,article,page');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor,tag');

    results.classList.add('is-loading');
    try {
      const html = await fetchSection('pg-predictive-search', url.toString(), { key: 'predictive-search' });
      if (input.value.trim() !== terms) return;
      const next = parseSection(html, '#pg-predictive-search');
      if (next) results.replaceChildren(next);
      results.dispatchEvent(new CustomEvent('pg:content:rendered', { bubbles: true }));
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') console.warn('[PetGuru] Predictive search failed', error);
    } finally {
      results.classList.remove('is-loading');
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeydown(event) {
    // A search input swallows the first Escape to clear itself; in an overlay, close instead.
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closest('dialog')?.close();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const links = [...this.refs.results.querySelectorAll('a')];
    if (!links.length) return;
    event.preventDefault();
    const index = links.indexOf(/** @type {HTMLAnchorElement} */ (document.activeElement));
    if (event.key === 'ArrowDown') links[Math.min(links.length - 1, index + 1)]?.focus();
    else if (index <= 0) this.refs.input.focus();
    else links[index - 1]?.focus();
  }
}

define('pg-search', PgSearch);
